import React, { useEffect, useState, useCallback } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import './config-styles.css';

// ── Phase 3: Schedule state helpers ───────────────────────────────────────────

const SCHEDULE_STATE_LABELS = {
  on: { label: 'Ativo', color: '#22c55e', bg: '#f0fdf4' },
  off: { label: 'Inativo', color: '#6b7280', bg: '#f9fafb' },
  forced_on: { label: 'Forçado On', color: '#3b82f6', bg: '#eff6ff' },
  forced_off: { label: 'Forçado Off', color: '#f59e0b', bg: '#fffbeb' },
};

function ScheduleBadge({ state }) {
  const cfg = SCHEDULE_STATE_LABELS[state] || SCHEDULE_STATE_LABELS.off;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.color}40`,
    }}>
      {cfg.label}
    </span>
  );
}

const getApiBase = async () => {
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

function AgentsConfig() {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [queues, setQueues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    extension: '',
    email: '',
    selectedSkills: [],
    selectedQueues: [],
  });
  // Phase 3: schedule state map { [agentKey]: ScheduleStateValue }
  const [scheduleStates, setScheduleStates] = useState({});
  const [forceLoading, setForceLoading] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-tenant-id': tenantId,
      };

      const [agentsRes, skillsRes, queuesRes] = await Promise.all([
        fetch(`${apiBase}/v2/callcenter/agents`, { headers }),
        fetch(`${apiBase}/v2/callcenter/skills`, { headers }),
        fetch(`${apiBase}/v2/callcenter/queues`, { headers }),
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        const agentsData = data.agents || data || [];
        setAgents(agentsData);
        // Phase 3: fetch schedule state for each agent (fire-and-forget, non-blocking)
        fetchScheduleStates(agentsData, apiBase, tenantId, token);
      }
      if (skillsRes.ok) {
        const data = await skillsRes.json();
        setSkills(data.skills || data || []);
      }
      if (queuesRes.ok) {
        const data = await queuesRes.json();
        setQueues(data.queues || data || []);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const url = editingAgent
        ? `${apiBase}/v2/callcenter/agents/${encodeURIComponent(editingAgent.agentKey)}`
        : `${apiBase}/v2/callcenter/agents`;

      const method = editingAgent ? 'PUT' : 'POST';

      const body = {
        displayName: formData.name,
        extension: formData.extension,
        email: formData.email,
        skills: formData.selectedSkills.map((sk) => ({
          skillKey: sk,
          level: 100,
        })),
        queues: formData.selectedQueues,
      };

      // Only include agentKey when creating (not when editing)
      if (!editingAgent) {
        body.agentKey = formData.extension;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingAgent(null);
        resetForm();
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao salvar agente: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao salvar agente:', e);
      alert('Erro ao salvar agente. Verifique sua conexão.');
    }
  };

  const handleDelete = async (agentKey) => {
    if (!window.confirm('Tem certeza que deseja excluir este agente?')) return;

    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const response = await fetch(`${apiBase}/v2/callcenter/agents/${encodeURIComponent(agentKey)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Erro ao excluir agente: ${errorData.message || response.statusText}`);
      }
    } catch (e) {
      console.error('Erro ao excluir agente:', e);
      alert('Erro ao excluir agente. Verifique sua conexão.');
    }
  };

  const openEditModal = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.displayName || agent.name || '',
      extension: agent.extension || agent.agentKey || '',
      email: agent.email || '',
      selectedSkills: agent.skills?.map((s) => s.skillKey) || [],
      selectedQueues: agent.queues || [],
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      extension: '',
      email: '',
      selectedSkills: [],
      selectedQueues: [],
    });
  };

  const openNewModal = () => {
    setEditingAgent(null);
    resetForm();
    setShowModal(true);
  };

  const toggleSkill = (skillKey) => {
    setFormData((prev) => ({
      ...prev,
      selectedSkills: prev.selectedSkills.includes(skillKey)
        ? prev.selectedSkills.filter((s) => s !== skillKey)
        : [...prev.selectedSkills, skillKey],
    }));
  };

  const toggleQueue = (queueKey) => {
    setFormData((prev) => ({
      ...prev,
      selectedQueues: prev.selectedQueues.includes(queueKey)
        ? prev.selectedQueues.filter((q) => q !== queueKey)
        : [...prev.selectedQueues, queueKey],
    }));
  };

  // Phase 3: fetch schedule states for all agents
  const fetchScheduleStates = useCallback(async (agentsList, apiBase, tenantId, token) => {
    if (!agentsList || agentsList.length === 0) return;
    const headers = { 'Authorization': `Bearer ${token}`, 'x-tenant-id': tenantId };
    const results = {};
    await Promise.allSettled(
      agentsList.map(async (agent) => {
        const key = agent.agentKey || agent.agent_key;
        if (!key) return;
        try {
          const res = await fetch(`${apiBase}/v2/callcenter/agents/${encodeURIComponent(key)}/schedule-state`, { headers });
          if (res.ok) {
            const data = await res.json();
            // Use the first state (null queue = global state)
            const globalState = data.states?.find(s => !s.queueKey) || data.states?.[0];
            results[key] = globalState?.currentState || null;
          }
        } catch { /* ignore — schedule state is non-critical */ }
      })
    );
    setScheduleStates(results);
  }, []);

  // Phase 3: force enable/disable an agent
  const handleForceSchedule = useCallback(async (agentKey, action) => {
    setForceLoading(prev => ({ ...prev, [agentKey]: action }));
    try {
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();
      const res = await fetch(`${apiBase}/v2/callcenter/agents/${encodeURIComponent(agentKey)}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'x-tenant-id': tenantId, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ttlMinutes: 240 }),
      });
      if (res.ok) {
        const expectedState = action === 'force-enable' ? 'forced_on'
          : action === 'force-disable' ? 'forced_off'
          : null;
        setScheduleStates(prev => ({ ...prev, [agentKey]: expectedState }));
      }
    } catch { /* non-critical */ }
    finally {
      setForceLoading(prev => ({ ...prev, [agentKey]: null }));
    }
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      available: '#22c55e',
      busy: '#ef4444',
      paused: '#8b5cf6',
      offline: '#6b7280',
      wrapup: '#f59e0b',
    };
    return colors[status] || colors.offline;
  };

  const getStatusLabel = (status) => {
    const labels = {
      available: 'Disponível',
      busy: 'Ocupado',
      paused: 'Pausado',
      offline: 'Offline',
      wrapup: 'Pós-atendimento',
    };
    return labels[status] || 'Offline';
  };

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Gestão de Agentes</h1>
          <p>Gerencie os agentes do Call Center e suas habilidades</p>
        </div>
        <button className="ccv2-btn ccv2-btn-primary" onClick={openNewModal}>
          + Novo Agente
        </button>
      </div>

      {loading ? (
        <div className="ccv2-config-loading">
          <div className="ccv2-spinner"></div>
          <p>Carregando agentes...</p>
        </div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Nome</th>
                  <th>Ramal</th>
                  <th>Skills</th>
                  <th>Filas</th>
                  <th>Escala</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="ccv2-empty-row">
                      Nenhum agente cadastrado
                    </td>
                  </tr>
                ) : (
                  agents.map((agent) => (
                    <tr key={agent.agentKey || agent.id_callcenter_agent}>
                      <td>
                        <span
                          className="ccv2-status-dot"
                          style={{ backgroundColor: getStatusColor(agent.status) }}
                          title={getStatusLabel(agent.status)}
                        />
                      </td>
                      <td className="ccv2-cell-name">{agent.displayName || agent.name}</td>
                      <td>{agent.extension || agent.agentKey}</td>
                      <td>
                        <div className="ccv2-tags">
                          {agent.skills?.slice(0, 3).map((skill) => (
                            <span key={skill.skillKey} className="ccv2-tag">
                              {skill.skillKey}
                            </span>
                          ))}
                          {agent.skills?.length > 3 && (
                            <span className="ccv2-tag ccv2-tag-more">
                              +{agent.skills.length - 3}
                            </span>
                          )}
                          {(!agent.skills || agent.skills.length === 0) && (
                            <span className="ccv2-tag ccv2-tag-empty">Nenhuma</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="ccv2-tags">
                          {agent.queues?.slice(0, 2).map((queue) => (
                            <span key={queue} className="ccv2-tag ccv2-tag-queue">
                              {queue}
                            </span>
                          ))}
                          {agent.queues?.length > 2 && (
                            <span className="ccv2-tag ccv2-tag-more">
                              +{agent.queues.length - 2}
                            </span>
                          )}
                          {(!agent.queues || agent.queues.length === 0) && (
                            <span className="ccv2-tag ccv2-tag-empty">Nenhuma</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {/* Phase 3: Schedule state badge */}
                        {scheduleStates[agent.agentKey] != null ? (
                          <ScheduleBadge state={scheduleStates[agent.agentKey]} />
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td className="ccv2-cell-actions">
                        <button
                          className="ccv2-btn-icon ccv2-btn-edit"
                          onClick={() => openEditModal(agent)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className="ccv2-btn-icon ccv2-btn-delete"
                          onClick={() => handleDelete(agent.agentKey)}
                          title="Excluir"
                        >
                          🗑️
                        </button>
                        {/* Phase 3: Force On/Off buttons */}
                        <button
                          className="ccv2-btn-icon"
                          style={{ fontSize: '13px', padding: '2px 6px', opacity: forceLoading[agent.agentKey] ? 0.5 : 1 }}
                          disabled={!!forceLoading[agent.agentKey]}
                          onClick={() => handleForceSchedule(agent.agentKey, 'force-enable')}
                          title="Forçar escala ON"
                        >
                          ▶
                        </button>
                        <button
                          className="ccv2-btn-icon"
                          style={{ fontSize: '13px', padding: '2px 6px', opacity: forceLoading[agent.agentKey] ? 0.5 : 1 }}
                          disabled={!!forceLoading[agent.agentKey]}
                          onClick={() => handleForceSchedule(agent.agentKey, 'force-disable')}
                          title="Forçar escala OFF"
                        >
                          ⏸
                        </button>
                        {(scheduleStates[agent.agentKey] === 'forced_on' || scheduleStates[agent.agentKey] === 'forced_off') && (
                          <button
                            className="ccv2-btn-icon"
                            style={{ fontSize: '11px', padding: '2px 6px' }}
                            onClick={() => handleForceSchedule(agent.agentKey, 'clear-force')}
                            title="Limpar force"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="ccv2-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ccv2-modal ccv2-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header">
              <h2>{editingAgent ? 'Editar Agente' : 'Novo Agente'}</h2>
              <button className="ccv2-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ccv2-modal-body">
                <div className="ccv2-form-row">
                  <div className="ccv2-form-group">
                    <label>Nome do Agente</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome completo"
                      required
                    />
                  </div>

                  <div className="ccv2-form-group">
                    <label>Ramal</label>
                    <input
                      type="text"
                      value={formData.extension}
                      onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
                      placeholder="Ex: 1001"
                      required
                    />
                  </div>
                </div>

                <div className="ccv2-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                </div>

                <div className="ccv2-form-group">
                  <label>Skills</label>
                  <div className="ccv2-checkbox-grid">
                    {skills.length === 0 ? (
                      <p className="ccv2-hint">Nenhuma skill cadastrada</p>
                    ) : (
                      skills.map((skill) => (
                        <label key={skill.skillKey} className="ccv2-checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.selectedSkills.includes(skill.skillKey)}
                            onChange={() => toggleSkill(skill.skillKey)}
                          />
                          <span>{skill.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="ccv2-form-group">
                  <label>Filas</label>
                  <div className="ccv2-checkbox-grid">
                    {queues.length === 0 ? (
                      <p className="ccv2-hint">Nenhuma fila cadastrada</p>
                    ) : (
                      queues.map((queue) => (
                        <label key={queue.queueKey} className="ccv2-checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.selectedQueues.includes(queue.queueKey)}
                            onChange={() => toggleQueue(queue.queueKey)}
                          />
                          <span>{queue.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="ccv2-modal-footer">
                <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ccv2-btn ccv2-btn-primary">
                  {editingAgent ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentsConfig;
